import { DEFAULT_FONT_FAMILY } from '@/data/constants/figureConstants';
import lodash from 'lodash';

/**
 * Find first item in array that matches provided matcher.
 *
 * Array.find only returns first message, array.findIndex only returns index. Sometimes
 * we need so we can use this function to avoid unnecessary looping.
 *
 * @param {Array} arr - Array to iterate
 * @param {Function} matcher - Callback function to match values
 * @param  {...any} args - Infinite optional arguments
 * @returns {Object} - Object containing message and it's index
 */
export function findWithIndex(arr, matcher, ...args) {
  for (let i = 0, l = arr.length; i < l; i++) {
    const item = arr[i];
    const match = matcher(item, ...args);
    if (match) return { item, index: i };
  }
}

/**
 * Returns the current data as a string to add to the file name of exported documents
 * @returns {string}
 */
export function fileDate() {
  const date = new Date();
  return `${date.getFullYear().toString().substring(2)}-${date.getMonth() + 1}-${date.getDate()}`;
}

/**
 * formats a string to Title Case
 * @param str
 * @returns {string}
 */
export function titleCase(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map(lodash.capitalize)
    .join(' ')
    .split('-')
    .map(lodash.startCase)
    .join('-');
}

/**
 * Either get current range or create a new one
 * @param {Selection} [sel] - Optional selection object
 * @returns {Range} - a range object
 */
export function getRange(sel = null) {
  // If no selection, get the current selection
  const selection = sel ? sel : window.getSelection();

  // If selection has a range return the range, otherwise create new range
  return selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();
}

export function clickElement(selection) {
  document.querySelector(selection).click();
}

/**
 * Returns start and end values of text within a node
 * @param {Node} node - DOM Node
 * @param {String} text - String to find start and end values of
 * @returns {{start: Number, end: Number}}
 */
export function getStartAndEnd(node, text) {
  const string = typeof node === 'string' ? node : node.textContent;
  const start = string.indexOf(text);
  const end = start + text.length;
  return { start, end };
}

/**
 * Abstraction for document.createElement
 *
 * createElement creates the specified element. Note the element has not been
 * inserted into the DOM yet, you would need to do that after the element is created
 *
 * document.creatElement docs: https://developer.mozilla.org/en-US/docs/Web/API/Document/createElement
 * @param {String} element - Denotes the element type to be created
 * @returns {HTMLElement} - The created element
 */
export function createElement(element) {
  return document.createElement(element);
}

/**
 * Abstraction for document.getElementById
 *
 * getElementById searches the DOM for an element with the provided ID. If it finds
 * one it will return that element. returns null if no element is found
 *
 * document.getElementByid docs: https://developer.mozilla.org/en-US/docs/Web/API/Document/getElementById
 * @param {String} id - Id to search for
 * @returns {HTMLElement}
 */
export function getElementById(id) {
  return document.getElementById(id);
}

/**
 * This triggers the file selector window on the users computer.
 *
 * The callback function will be trigger once a user selects a file. the selected
 * file will be passed to the callback function.
 *
 * This is used in our illustration upload, profile image upload, and document upload
 * @param cb - Callback function
 * @param {String} [fileType] - What file types we should allow the user to select. default is any image type
 */
export function selectFile(cb, fileType = 'image/*') {
  const $temp = getElementById('tempInput');
  $temp?.remove();

  const $input = createElement('input');
  $input.id = 'tempInput';
  $input.type = 'file';
  $input.accept = fileType;
  $input.addEventListener('change', cb);
  $input.click();
}

/**
 * This function creates an HTML Image element using the provided data url. We are
 * able to view dimension data about the image from the HTML image element
 *
 * This is used in the diagram helpers to scale the image up or down to fit
 * within the bounds of the editor
 *
 * data URl docs: https://developer.mozilla.org/en-US/docs/web/http/basics_of_http/data_urls
 * HTMLImageElement Docs: https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement
 * @param dataUrl
 * @returns {Promise<HTMLImageElement>}
 */
export function getImageData(dataUrl) {
  return new Promise((res) => {
    const image = new Image();

    image.onload = () => {
      res(image);
    };

    image.src = dataUrl;
  });
}

/**
 * passes the result of readAsDataURl to the callback function. We do this to get
 * the data URL of a file. This is primarily used in the profile overlay when a user
 * uploads a profile image and when the user adds illustrations to the figures.
 *
 * Learn more about data URLs here: https://developer.mozilla.org/en-US/docs/web/http/basics_of_http/data_urls
 * @param file
 * @param cb
 */
export function readDataUrl(file, cb) {
  const reader = new FileReader();
  reader.onloadend = () => {
    cb(reader.result);
  };

  reader.readAsDataURL(file);
}

// START REGION STRING HELPERS

/**
 * FIXME: Safe to remove?
 * Reverse order of a string
 * @param {String} str - String to reverse
 * @returns {String} - reversed string
 */
export function reverseString(str) {
  return str.split('').reverse().join('');
}

/**
 * Use lodash.truncate() to truncate a string at the provided length
 * @param {string} text - String to truncate
 * @param {number} length - Length to truncate string at
 * @returns {string} - Truncate string
 */
export function truncate(text, length) {
  return lodash.truncate(text, { length });
}

/**
 * Calculate the maximum characters before wrapping in a container.
 *
 * Note: This is hard to do because character widths can vary from character to character in
 * non-mono-spaced fonts. As a result this is more of an estimation and not 100% accurate.
 *
 * This tends to be most accurate when calculating character width from a string we intend to insert into the container
 * @param text - Text to calculate the character width from.
 * @param containerWidth - Width of the container to calculate maximum characters for.
 * @param fontSize - Font size of the text
 * @returns {number} - Estimated maximum characters
 */
export function getMaxChars(text, containerWidth, fontSize) {
  const textWidth = getTextWidth(text, fontSize);
  const charWidth = Math.round(textWidth / text.length);
  return Math.floor(containerWidth / charWidth) - 2;
}

/**
 * Check if the provided text will wrap inside a container
 * @param text - Text to calculate the width of
 * @param containerWidth - Width of the container
 * @param fontSize - Font size of the text
 * @returns {boolean} - True if text will wrap, false if it will not.
 */
export function checkWillWrap(text, containerWidth, fontSize) {
  const textWidth = getTextWidth(text, fontSize);
  return textWidth > containerWidth;
}

/**
 * Calculate the width of a string at the provided text size
 * @param text - String to measure
 * @param fontSize - Font size of the string
 * @returns {number} - Width of the string
 */
export function getTextWidth(text, fontSize) {
  // Create the div element to insert text into
  const div = document.createElement('div');

  // Set element styles
  div.textContent = text;
  div.style.width = 'fit-content';
  div.style.fontSize = `${fontSize}px`;
  div.style.fontFamily = DEFAULT_FONT_FAMILY;

  // Attach to the DOM
  document.body.appendChild(div);

  // Get width of div then remove it from DOM
  const width = div.clientWidth;
  div.remove();
  return width;
}

/**
 * This ensures that error messages are formatted correctly with proper punctuation
 * and includes the errorID. We do this because we can get errors from multiple sources
 * and we want to ensure that error messages are always displayed consistently
 * @param message
 * @param errorId
 * @returns {string}
 */
export function formatErrorMessage(message, errorId) {
  // Ensuring the end of the message has a period
  message = message.trim().replace(/\.?$/, '.');
  errorId = errorId ? errorId.trim().replace(/\.?$/, '.') : errorId;

  return errorId ? `${message} Error ID: ${errorId}` : `${message}`;
}

/**
 * Remove all tags from a string.
 * @param {string} string
 * @returns {string}
 */
export function removeTags(string) {
  if (!string) return '';
  const tagRegex = /(<\/?[a-z]+>)/gi;
  return string.replace(tagRegex, '');
}

// END REGION STRING HELPERS

// START REGION REGEX HELPERS
